import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, RegularButton, Typography } from 'fronton-react';
import { cx } from 'linaria';
import { useSelector } from 'react-redux';
import { RegionChangeDialog } from 'features/region-change-dialog';
import { getRegionId } from 'features/page-model/ducks/get-model';
import { Infobar } from 'common/components/infobar';
import { OperationalTime } from 'common/components/operational-time';
import {
  sendInfobarHintShowGA,
  sendInfobarShowGA,
  sendPhoneGA,
  sendStorePageButtonGA,
} from 'features/stores/utils/ga';
import { patchUrlByLocale } from 'utils';
import { SHOP_URL } from 'common/constants';
import { styles } from './style';
import { StoreInfoProps } from '../store-item/types';

export const StoreInfo: React.FC<StoreInfoProps> = ({
  id,
  name,
  address,
  operationalTime,
  gardenCenterOperationalTime,
  buildingYardOperationalTime,
  pickupOperationalTime,
  phone,
  importantNotificationTitle,
  importantNotificationDescription,
  url,
  storesRegionId,
  active,
}) => {
  const timeSlots =
    operationalTime || gardenCenterOperationalTime || buildingYardOperationalTime || pickupOperationalTime;
  const { t } = useTranslation();
  const currentRegionId = useSelector(getRegionId) || '';
  const [changeRegionModal, setChangeRegionModal] = useState(false);
  const differentRegion = storesRegionId !== currentRegionId;
  const shopPage = t('shopPage');
  const workingHours = t('workingHours');

  const sentPhoneGa = useCallback(() => sendPhoneGA(phone, name), [name, phone]);

  const handleINHintShow = useCallback(
    () => sendInfobarHintShowGA(name, importantNotificationTitle),
    [name, importantNotificationTitle],
  );

  const handleStorePageButton = useCallback(() => {
    if (differentRegion) setChangeRegionModal(() => true);
    sendStorePageButtonGA(shopPage, name);
  }, [differentRegion, name, shopPage]);

  useEffect(() => {
    if (importantNotificationTitle && active) {
      sendInfobarShowGA(name, importantNotificationTitle);
    }
  }, [active, importantNotificationTitle, name]);

  return (
    <div className={cx(styles.info, active && styles.openInfo)} data-qa="store-item-info">
      {importantNotificationTitle && (
        <Infobar
          message={importantNotificationTitle}
          hint={importantNotificationDescription}
          className={styles.warning}
          onShowHint={handleINHintShow}
        />
      )}
      {address && (
        <Typography
          color="text-primary"
          className={styles.infoAddress}
          variant="m"
          size="body_short"
          itemProp="address"
          itemType="https://schema.org/Text"
        >
          {address}
        </Typography>
      )}
      {timeSlots && (
        <>
          <Divider className={styles.divider} />
          <Typography variant="s" size="subtitle" color="text-primary">
            {workingHours}
          </Typography>
          <div className={styles.timeTable}>
            {operationalTime && (
              <OperationalTime label={`${t('tradeFloor')}:`} timeRange={operationalTime} />
            )}
            {gardenCenterOperationalTime && (
              <OperationalTime label={`${t('gardenCenter')}:`} timeRange={gardenCenterOperationalTime} />
            )}
            {buildingYardOperationalTime && (
              <OperationalTime label={`${t('buildingYard')}:`} timeRange={buildingYardOperationalTime} />
            )}
            {pickupOperationalTime && (
              <OperationalTime label={`${t('pickup')}:`} timeRange={pickupOperationalTime} />
            )}
          </div>
        </>
      )}
      {phone && (
        <>
          <Divider className={styles.divider} />
          <Typography variant="s" size="subtitle" color="text-primary">
            {t('forCustomers')}
          </Typography>
          <div className={styles.phoneSection}>
            <Typography variant="s" size="body_short" color="text-primary">
              {t('support')}:
            </Typography>
            <Divider variant="dashed" />
            <a
              href={`tel:${phone}`}
              className={styles.phoneNumber}
              onClick={sentPhoneGa}
              data-qa="contact-number"
            >
              <Typography variant="s" size="body_short" itemProp="telephone">
                {phone}
              </Typography>
            </a>
          </div>
        </>
      )}
      {url && (
        <>
          <Divider className={styles.divider} />
          {differentRegion ? (
            <RegularButton
              data-qa="store-info-shop-button"
              wide
              variant="outline"
              size="l"
              onClick={handleStorePageButton}
            >
              {t('shopPage')}
            </RegularButton>
          ) : (
            <RegularButton
              data-qa="store-info-shop-button"
              wide
              variant="outline"
              size="l"
              onClick={handleStorePageButton}
              itemProp="url"
              href={patchUrlByLocale(`${SHOP_URL}/${url}`)}
              target="_blank"
            >
              {t('shopPage')}
            </RegularButton>
          )}
          {differentRegion && (
            <RegionChangeDialog
              active={changeRegionModal}
              closeHandle={() => setChangeRegionModal(false)}
              targetRegionId={storesRegionId}
              targetStoreId={id}
            />
          )}
        </>
      )}
    </div>
  );
};
